import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMfgApi, errMsg } from './mfgApi';
import { useToast } from '../../hooks/useToast';
import { Plus, Loader2, X, AlertTriangle } from 'lucide-react';
import { useMfgRole, MfgReadOnlyBadge } from './useMfgRole';

export default function MfgDispatch() {
  const mfg = useMfgApi();
  const toast = useToast();
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(sp.get('new') === '1');
  const [orders, setOrders] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const { canWrite } = useMfgRole();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [disp, o, d] = await Promise.all([
        mfg.get('/dispatches', { limit: 100 }),
        mfg.get('/client-orders'), mfg.get('/destinations'),
      ]);
      setRows(disp.items || disp || []);
      setOrders(o.items || o || []);
      setDestinations((d.items || d || []).filter(x => x.is_active !== false));
    } catch { toast.show('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="mfg-dispatch-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">Dispatches<MfgReadOnlyBadge /></h1>
        {canWrite && <button data-testid="mfg-new-disp-btn" onClick={() => setShowNew(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-cyan-500 text-white text-sm font-bold hover:bg-cyan-600 transition-colors"><Plus size={14} /> Record Dispatch</button>}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="dispatch-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <th className="text-left py-2.5 px-3">Date</th><th className="text-left py-2.5 px-3">Order</th>
              <th className="text-left py-2.5 px-3">Destination</th><th className="text-right py-2.5 px-3">Cartons</th>
              <th className="text-right py-2.5 px-3">Pieces</th><th className="text-left py-2.5 px-3">Vehicle</th>
              <th className="text-left py-2.5 px-3">Driver</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">No dispatches yet.</td></tr> : rows.map(r => (
                <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`disp-row-${r.id}`}>
                  <td className="py-2.5 px-3 text-gray-700">{r.dispatch_date}</td>
                  <td className="py-2.5 px-3 font-medium text-gray-900">{r.order_no || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-600">{r.destination_name || '—'}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-gray-900">{r.quantity_cartons}</td>
                  <td className="py-2.5 px-3 text-right text-gray-600">{r.quantity_pieces?.toLocaleString() || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-500">{r.vehicle_no || '—'}</td>
                  <td className="py-2.5 px-3 text-gray-500">{r.driver_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && canWrite && <NewDispatchModal orders={orders} destinations={destinations} mfg={mfg} toast={toast} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewDispatchModal({ orders, destinations, mfg, toast, onClose, onCreated }) {
  const [form, setForm] = useState({ client_order_id: '', destination_id: '', dispatch_date: new Date().toISOString().split('T')[0], quantity_cartons: '', vehicle_no: '', driver_name: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stockError, setStockError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const selectedOrder = orders.find(o => o.id === form.client_order_id);
  const packSize = selectedOrder?.carton_pack_size || 0;
  const previewPieces = (Number(form.quantity_cartons) || 0) * packSize;

  const submit = async () => {
    setSaving(true); setError(''); setStockError(null);
    try {
      await mfg.post('/dispatches', {
        client_order_id: form.client_order_id, destination_id: form.destination_id,
        dispatch_date: form.dispatch_date, quantity_cartons: Number(form.quantity_cartons),
        vehicle_no: form.vehicle_no || undefined, driver_name: form.driver_name || undefined,
        notes: form.notes || undefined,
      });
      toast.show('Dispatch recorded', 'success'); onCreated();
    } catch (e) {
      if (e.status === 409 && e.detail?.code === 'insufficient_fg_stock') {
        setStockError(`Only ${e.detail.available_cartons} cartons available — reduce quantity.`);
      } else { setError(errMsg(e)); }
    } finally { setSaving(false); }
  };

  const valid = form.client_order_id && form.destination_id && Number(form.quantity_cartons) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="new-dispatch-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-extrabold text-gray-900">Record Dispatch</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="new-disp-close"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <Fl label="Client Order"><select data-testid="disp-order" className="input-sm" value={form.client_order_id} onChange={e => set('client_order_id', e.target.value)}><option value="">Select order...</option>{orders.map(o => <option key={o.id} value={o.id}>{o.order_no} — {o.client_name} ({o.sku_name || o.sku_code})</option>)}</select></Fl>
          <Fl label="Destination"><select data-testid="disp-dest" className="input-sm" value={form.destination_id} onChange={e => set('destination_id', e.target.value)}><option value="">Select destination...</option>{destinations.map(d => <option key={d.id} value={d.id}>{d.name}{d.city ? ` — ${d.city}` : ''}</option>)}</select></Fl>
          <div className="grid grid-cols-2 gap-3">
            <Fl label="Date"><input data-testid="disp-date" type="date" className="input-sm" value={form.dispatch_date} onChange={e => set('dispatch_date', e.target.value)} /></Fl>
            <Fl label="Cartons"><input data-testid="disp-qty" type="number" min="1" className="input-sm" value={form.quantity_cartons} onChange={e => set('quantity_cartons', e.target.value)} /></Fl>
          </div>
          {packSize > 0 && Number(form.quantity_cartons) > 0 && (
            <p className="text-xs text-indigo-600 font-medium bg-indigo-50 rounded-lg px-3 py-1.5" data-testid="disp-preview">This will dispatch {previewPieces.toLocaleString()} pieces (pack size {packSize})</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Fl label="Vehicle No"><input data-testid="disp-vehicle" className="input-sm" value={form.vehicle_no} onChange={e => set('vehicle_no', e.target.value)} placeholder="Optional" /></Fl>
            <Fl label="Driver Name"><input data-testid="disp-driver" className="input-sm" value={form.driver_name} onChange={e => set('driver_name', e.target.value)} placeholder="Optional" /></Fl>
          </div>
          {stockError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="disp-stock-error">
              <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs font-medium text-red-700">{stockError}</p>
            </div>
          )}
          {error && <p className="text-red-500 text-xs font-medium" data-testid="disp-error">{error}</p>}
        </div>
        <button data-testid="disp-submit" onClick={submit} disabled={saving || !valid} className="w-full mt-4 h-11 rounded-xl bg-cyan-500 text-white font-bold text-sm hover:bg-cyan-600 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Record Dispatch'}
        </button>
      </div>
    </div>
  );
}

function Fl({ label, children }) {
  return <label className="block"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</span><div className="mt-1">{children}</div></label>;
}
